<template>
  <v-app class="app-background">
    <!-- Status bar spacer - creates space for the status bar -->
    <div class="status-bar-spacer" :style="statusBarSpacerStyle"></div>
    
    <v-main class="main-content">
      <div class="content-wrapper" :style="contentWrapperStyle">
        <NotificationPermissionDialog v-model="showNotificationDialog" />
        
        <router-view v-slot="{ Component }">
          <v-slide-x-transition mode="out-in">
            <component :is="Component" />
          </v-slide-x-transition>
        </router-view>
      </div>
      
      <BottomNavigation 
        ref="bottomNavRef" 
        v-if="showBottomNav" 
        :selected="selected"
        @update:selected="selected = $event"
        :style="bottomNavStyle"
      />
    </v-main>
    <AppDialog />
    <AppToaster />
    <AppBanner />
  </v-app>
</template>

<script>
import AppDialog from '@/components/general/ui/AppDialog.vue'
import AppToaster from '@/components/general/ui/AppToaster.vue'
import AppBanner from '@/components/general/ui/AppBanner.vue'
import { askNotificationPermissionOnce } from '@/services/localNotify'
import { startAppUpdateChecks } from '@/services/appUpdateCheck'
import { initGlobalScanner } from '@/services/BleScannerManager'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { loadConfig } from '@/configLoader'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { StatusBar, Style } from '@capacitor/status-bar'
import { Capacitor } from '@capacitor/core'

import BottomNavigation from '@/components/general/BottomNavigation'
import NotificationPermissionDialog from '@/components/general/ChildComponents/NotificationPermissionDialog.vue'
import { checkNotificationPermission } from '@/services/utils'
import { setupForegroundListener, requestNotificationPermission } from '@/FirebaseMessagingService'
import { useOAuthService } from './services/OAuthService'
import { getDeviceFingerprint } from '@/services/BlueIDService'


export default {
  components: {
    AppDialog,
    AppToaster,
    AppBanner,
    BottomNavigation,
    NotificationPermissionDialog,
  },
  setup() {
    const route = useRoute()
    const selected = ref(0)
    const bottomNavRef = ref(null)
    const bottomNavHeight = ref(0)
    const showNotificationDialog = ref(false)
    const { isLoggedIn, getUserProfile } = useOAuthService()
    
    // Platform detection
    const isAndroid = Capacitor.getPlatform() === 'android'
    const isIOS = Capacitor.getPlatform() === 'ios'
    
    // Tablet detection - checks if device is likely a tablet
    const isTablet = ref(false)
    
    const detectTablet = () => {
      // Check screen size - tablets typically have width >= 600dp
      const screenWidth = window.screen.width
      const screenHeight = window.screen.height
      const minDimension = Math.min(screenWidth, screenHeight)
      
      // For Android, consider it a tablet if smallest dimension is >= 600px
      // This is the standard Android tablet breakpoint
      if (isAndroid && minDimension >= 600) {
        return true
      }
      
      // Additional check: aspect ratio and pixel density
      const pixelRatio = window.devicePixelRatio || 1
      const dpi = pixelRatio * 160 // approximate DPI
      
      // Tablets typically have larger physical screens
      // If width in inches (pixels / dpi) is > 7", it's likely a tablet
      const widthInches = screenWidth / dpi
      
      return widthInches > 7
    }
    
    // ⚙️ STATUS BAR CONFIGURATION - Android
    const STATUS_BAR_CONFIG = {
      backgroundColor: '#000000',  // Black background
      textStyle: Style.Dark,        // Dark = white text, Light = black text
    }
    
    // Safe area insets from native plugin
    const safeAreaInsets = ref({
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    })

    const showBottomNav = computed(() =>
      route.name !== 'Login' &&
      !window.location.href.includes('/oauth2') &&
      !window.location.href.includes('/home') &&
      !window.location.href.includes('/onevr') &&
      !window.location.href.includes('/request-demo'
    )
    )
    // Status bar spacer with matching background color - ANDROID ONLY, NOT FOR TABLETS
    const statusBarSpacerStyle = computed(() => {
      if (!isAndroid || isTablet.value) {
        return { height: '0px' }  // No spacer on iOS or tablets
      }
      return {
        height: `${safeAreaInsets.value.top}px`,
        backgroundColor: STATUS_BAR_CONFIG.backgroundColor,
      }
    })

    // Content wrapper - Apply safe area only on Android phones, not tablets
    const contentWrapperStyle = computed(() => {
      const bottomPadding = showBottomNav.value 
        ? bottomNavHeight.value 
        : 0
      
      // Only apply safe area insets on Android PHONES (not tablets)
      if (isAndroid && !isTablet.value) {
        return {
          paddingBottom: `${bottomPadding + safeAreaInsets.value.bottom}px`,
          paddingLeft: `${safeAreaInsets.value.left}px`,
          paddingRight: `${safeAreaInsets.value.right}px`,
          minHeight: `calc(100vh - ${safeAreaInsets.value.top}px)`,
          boxSizing: 'border-box',
        }
      } else {
        // iOS and Android tablets: original behavior with just bottom nav padding
        return {
          paddingBottom: `${bottomPadding}px`,
          minHeight: '100vh',
          boxSizing: 'border-box',
        }
      }
    })

    // Bottom navigation style - Apply safe area only on Android phones, not tablets
    const bottomNavStyle = computed(() => {
      const baseStyle = {
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        zIndex: '1000',
      }
      
      // Only apply bottom padding on Android PHONES
      if (isAndroid && !isTablet.value) {
        return {
          ...baseStyle,
          paddingBottom: `${safeAreaInsets.value.bottom}px`,
        }
      }
      
      return baseStyle
    })

    // Setup status bar
    const setupStatusBar = async () => {
      if (!Capacitor.isNativePlatform()) return

      const platform = Capacitor.getPlatform()

      try {
        if (platform === 'android') {
          await StatusBar.show()
          await StatusBar.setOverlaysWebView({ overlay: false })
          await StatusBar.setStyle({ style: STATUS_BAR_CONFIG.textStyle })
          await StatusBar.setBackgroundColor({ color: STATUS_BAR_CONFIG.backgroundColor })
          console.log('Android StatusBar configured:', STATUS_BAR_CONFIG)
        } else if (platform === 'ios') {
          // Dark style = white icons, visible on dark backgrounds
          await StatusBar.setStyle({ style: Style.Dark })
          console.log('iOS StatusBar style set to Dark (white icons)')
        }
      } catch (error) {
        console.warn('StatusBar setup failed:', error)
      }
    }

    // Setup safe area plugin - ANDROID PHONES ONLY (not tablets)
    const setupSafeArea = async () => {
      // Detect if device is a tablet
      isTablet.value = detectTablet()
      
      console.log('Device detection:', {
        platform: Capacitor.getPlatform(),
        isTablet: isTablet.value,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        minDimension: Math.min(window.screen.width, window.screen.height)
      })
      
      // Only use SafeArea plugin on Android PHONES (not tablets)
      if (!isAndroid || isTablet.value) {
        console.log('Skipping SafeArea setup - iOS or Android tablet detected')
        return
      }
      
      try {
        const { insets } = await SafeArea.getSafeAreaInsets()
        safeAreaInsets.value = insets
        
        for (const [key, value] of Object.entries(insets)) {
          document.documentElement.style.setProperty(
            `--safe-area-inset-${key}`,
            `${value}px`
          )
        }

        await SafeArea.addListener('safeAreaChanged', (data) => {
          safeAreaInsets.value = data.insets
          
          for (const [key, value] of Object.entries(data.insets)) {
            document.documentElement.style.setProperty(
              `--safe-area-inset-${key}`,
              `${value}px`
            )
          }
        })

        console.log('Android Phone Safe Area Insets:', insets)
      } catch (error) {
        console.error('SafeArea plugin error:', error)
      }
    }

    onMounted(async () => {
      try {
        await setupStatusBar()
        await setupSafeArea()


        const config = await loadConfig()

        if (bottomNavRef.value) {
          bottomNavHeight.value = bottomNavRef.value.$el.clientHeight
        }
        initGlobalScanner().catch(err => console.warn('BleScannerManager init error:', err))
      } catch (e) {
        console.error('App.vue onMounted error:', e)
      }
    })

    onUnmounted(async () => {
      try {
        await SafeArea.removeAllListeners()
      } catch (error) {
        console.error('Error removing SafeArea listeners:', error)
      }
    })
    
    // After login: ask for notifications once, then start the store update checks, so a
    // found update can already use the answer.
    // PIA's start page (/home) hides the bottom bar, so this keys off login, not the bar.
    const onAppPage = computed(() =>
      Boolean(route.name) && route.name !== 'Login' && !window.location.href.includes('/oauth2')
    )
    watch(
      onAppPage,
      async loggedIn => {
        if (!loggedIn) return
        await askNotificationPermissionOnce().catch(() => {})
        startAppUpdateChecks()
      },
      { immediate: true }
    )

    watch(
      () => bottomNavRef.value,
      newVal => {
        if (newVal) {
          bottomNavHeight.value = newVal.$el.clientHeight
        }
      },
      { immediate: true }
    )

    return {
      selected,
      showBottomNav,
      bottomNavRef,
      bottomNavHeight,
      showNotificationDialog,
      contentWrapperStyle,
      bottomNavStyle,
      safeAreaInsets,
      statusBarSpacerStyle,
      isAndroid,
      isIOS,
      isTablet,
    }
  },
}
</script>

<style>
:root {
  --safe-area-inset-top: 0px;
  --safe-area-inset-right: 0px;
  --safe-area-inset-bottom: 0px;
  --safe-area-inset-left: 0px;
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

#app {
  width: 100%;
  height: 100%;
}

.v-application {
  width: 100%;
  height: 100%;
}
</style>

<style scoped>
.app-background {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.status-bar-spacer {
  width: 100%;
  flex-shrink: 0;
}

.main-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}
</style>
<!-- App.vue Component

This component serves as the main layout for the application. It provides a structured and dynamic user interface that adapts based on the current route and user interactions.

The main features of this component include:

1. Bottom Navigation: A dynamic bottom navigation bar that appears conditionally based on the current route. It is hidden on the 'Login' route and any routes containing '/oauth2' or '/home'. The navigation bar allows users to switch between different sections of the app such as Info Center, Building, Booking, Chat, and Menu. The selected tab is highlighted, providing visual feedback to the user.

2. Router View with Transition: The main content area of the app is managed by Vue Router. The <router-view> component dynamically loads the appropriate component based on the current route. A slide transition effect is applied to enhance the user experience when navigating between different views.

3. Dynamic Padding with Tablet Detection: The main content area adjusts its bottom padding dynamically based on the height of the bottom navigation bar. Safe area insets are ONLY applied to Android phones (not tablets) to handle edge-to-edge displays and notches. Tablets use standard padding without safe area adjustments.

4. Data Fetching and State Management: On component mount, the app fetches and stores the selected building information. It uses utility functions to get and set stored items and fetches building data from an API. This ensures that the app has the necessary data to function correctly and provides a personalized experience for the user.

5. Visual Feedback: The component logs important events and errors to the console, helping developers debug and understand the app's behavior. Additionally, the component's background is styled with a gradient, providing a visually appealing interface.

Users can interact with this component by:
- Navigating between different sections using the bottom navigation bar.
- Viewing dynamic content that changes based on the selected route.
- Experiencing smooth transitions between different views.
- Receiving visual feedback through the highlighted navigation tabs and the styled background.

Overall, this component enhances the user experience by providing a structured, dynamic, and visually appealing interface that adapts to user interactions and route changes, with proper tablet detection to prevent excessive padding on larger Android devices.
-->
